import React, { useState, useEffect } from 'react';
import { useLocalization } from '../../contexts/LocalizationContext';
import { Wallet, Clock, ChevronDown, BarChart3, PieChart, TrendingUp as TrendingUpIcon, Info } from 'lucide-react';
import { DashboardStats, BalanceData, OperatorStats as OperatorStatsType } from '../../services/dashboardService';
import WalletDetailsModal from './WalletDetailsModal';


interface OperatorStatsProps {
  dashboardData: DashboardStats | null;
  balanceData: BalanceData | null;
  operatorStatsData: OperatorStatsType | null;
  loading: boolean;
  selectedCurrency?: string;
  onCurrencyChange?: (currency: string) => void;
}

interface ActionStats {
  successful: number;
  failed: number;
  pending: number;
  submitted: number;
  total: number;
}

interface OperatorStats {
  successful: number;
  failed: number;
  pending: number;
  submitted: number;
  deposit?: ActionStats;
  withdrawal?: ActionStats;
}

interface OperatorData {
  name: string;
  gradient: string;
  stats: OperatorStats;
  balance: {
    deposit: number;
    withdrawal: number;
    reservedDeposit: number;
    reservedWithdrawal: number;
    available: number;
    inFlight: number;
    total: number;
    currency: string;
  };
  successRate: number;
  trend: number;
}

const OperatorStats: React.FC<OperatorStatsProps> = ({
  dashboardData,
  balanceData,
  operatorStatsData,
  loading,
  selectedCurrency: selectedCurrencyProp,
  onCurrencyChange,
}) => {
  const { t, countryConfig, formatCurrency } = useLocalization();
  // Controlled-or-uncontrolled: prefer parent state if provided.
  const [internalCurrency, setInternalCurrency] = useState<string>('CDF');
  const selectedCurrency = selectedCurrencyProp ?? internalCurrency;
  const setSelectedCurrency = (c: string) => {
    if (onCurrencyChange) onCurrencyChange(c);
    else setInternalCurrency(c);
  };
  const [activeTab, setActiveTab] = useState<'cards' | 'charts'>('cards');
  const [selectedOperator, setSelectedOperator] = useState<OperatorData | null>(null);

  // Configuration avec couleurs plus douces et harmonieuses
  const operatorConfig: { [key: string]: { 
    gradient: string; 
    icon: string;
    trend: number;
    textColor: string;
    color: string;
  } } = {
    'airtel': {
      gradient: 'linear-gradient(135deg, #667eea 0%, #5a67d8 100%)',
      icon: '/logos/airtel.webp',
      trend: 12,
      textColor: 'text-blue-50',
      color: '#667eea'
    },
    'orange': {
      gradient: 'linear-gradient(135deg, #ed8936 1%, #dd6b20 100%)',
      icon: '/logos/orange.webp',
      trend: 8,
      textColor: 'text-orange-50',
      color: '#ed8936'
    },
    'mpesa': {
      gradient: 'linear-gradient(135deg, #38a169 0%, #2f855a 100%)',
      icon: '/logos/mpesa.webp',
      trend: 15,
      textColor: 'text-green-50',
      color: '#38a169'
    },
    'afrimoney': {
      gradient: 'linear-gradient(135deg, #9f7aea 0%, #805ad5 100%)',
      icon: '/logos/afrimoney.webp',
      trend: 5,
      textColor: 'text-purple-50',
      color: '#9f7aea'
    }
  };

  const getDefaultConfig = () => ({
    gradient: 'linear-gradient(135deg, #718096 0%, #4a5568 100%)',
    icon: '/logos/default.webp',
    trend: 0,
    textColor: 'text-gray-50',
    color: '#718096'
  });

  // Note: we used to auto-reset selectedCurrency to 'CDF' on every data
  // change here, which clobbered the user's USD selection right after the
  // refetch returned. The parent now owns the currency state, so we leave
  // the user's choice alone.

  const getOperatorsFromBackendData = (): OperatorData[] => {
    if (operatorStatsData?.operator_stats && balanceData?.balances) {
      return transformOperatorStatsData();
    }
    
    if (dashboardData?.by_operator && balanceData?.balances) {
      return transformDashboardData();
    }

    return [];
  };

  useEffect(() => {
  console.log('=== DEBUG CURRENCIES ===');
  console.log('Operator Stats Data:', operatorStatsData);
  console.log('Dashboard Data:', dashboardData);
  console.log('Balance Data:', balanceData);
  
  const currencies = getAvailableCurrencies();
  console.log('Available Currencies:', currencies);
  console.log('Selected Currency:', selectedCurrency);
}, [operatorStatsData, dashboardData, balanceData, selectedCurrency]);

  const transformOperatorStatsData = (): OperatorData[] => {
    const operators: OperatorData[] = [];
    const requiredMethods = ['airtel', 'orange', 'mpesa', 'afrimoney'];
    
    // Create maps for quick access - maintenant organisé par action
    const operatorMap = new Map();
    operatorStatsData!.operator_stats.forEach(op => {
      const key = `${op.method.toLowerCase()}_${op.currency}_${op.action}`;
      operatorMap.set(key, op);
    });

    const balanceMap = new Map();
    balanceData!.balances.forEach(bal => {
      const key = `${bal.method.toLowerCase()}_${bal.currency}`;
      balanceMap.set(key, bal);
    });

    requiredMethods.forEach(method => {
      ['CDF', 'USD'].forEach(currency => {
        const key = `${method}_${currency}`;
        const operatorBalance = balanceMap.get(key);

        // Apply currency filter
        if (currency !== selectedCurrency) {
          return;
        }

        // Récupérer les données séparées par action
        const depositKey = `${method}_${currency}_debit`;
        const withdrawalKey = `${method}_${currency}_credit`;
        
        const depositData = operatorMap.get(depositKey);
        const withdrawalData = operatorMap.get(withdrawalKey);

        const depositBalance = operatorBalance?.realtime_deposit || 0;
        const withdrawalBalance = operatorBalance?.realtime_withdrawal || 0;
        const reservedDeposit = operatorBalance?.reserved_deposit || 0;
        const reservedWithdrawal = operatorBalance?.reserved_withdrawal || 0;
        const availableBalance = operatorBalance?.available !== undefined
          ? operatorBalance.available
          : (depositBalance - reservedDeposit) + (withdrawalBalance - reservedWithdrawal);
        const inFlightBalance = operatorBalance?.in_flight !== undefined
          ? operatorBalance.in_flight
          : reservedDeposit + reservedWithdrawal;
        const totalBalance = operatorBalance?.total !== undefined
          ? operatorBalance.total
          : depositBalance + withdrawalBalance;

        const config = operatorConfig[method] || getDefaultConfig();

        // Calculs séparés pour dépôt et retrait
        const depositSuccessful = depositData?.successful || 0;
        const depositFailed = depositData?.failed || 0;
        const depositPending = depositData?.pending || 0;
        const depositSubmitted = depositData?.submitted || 0;
        
        const withdrawalSuccessful = withdrawalData?.successful || 0;
        const withdrawalFailed = withdrawalData?.failed || 0;
        const withdrawalPending = withdrawalData?.pending || 0;
        const withdrawalSubmitted = withdrawalData?.submitted || 0;

        // Totaux combinés pour l'affichage
        const totalSuccessful = depositSuccessful + withdrawalSuccessful;
        const totalFailed = depositFailed + withdrawalFailed;
        const totalPending = depositPending + withdrawalPending;
        const totalSubmitted = depositSubmitted + withdrawalSubmitted;
        
        const totalTransactions = totalSuccessful + totalFailed;
        const successRate = totalTransactions > 0 ? (totalSuccessful / totalTransactions) * 100 : 0;

        // Only create if we have data or if it's the selected currency
        if (totalSuccessful > 0 || totalFailed > 0 || totalPending > 0 || totalSubmitted > 0 || totalBalance > 0 || selectedCurrency === currency) {
          operators.push({
            name: method.charAt(0).toUpperCase() + method.slice(1),
            gradient: config.gradient,
            stats: {
              successful: totalSuccessful,
              failed: totalFailed,
              pending: totalPending,
              submitted: totalSubmitted,
              // Ajout des stats détaillées par action
              deposit: {
                successful: depositSuccessful,
                failed: depositFailed,
                pending: depositPending,
                submitted: depositSubmitted,
                total: depositData?.total_amount || 0
              },
              withdrawal: {
                successful: withdrawalSuccessful,
                failed: withdrawalFailed,
                pending: withdrawalPending,
                submitted: withdrawalSubmitted,
                total: withdrawalData?.total_amount || 0
              }
            },
            balance: {
              deposit: depositBalance,
              withdrawal: withdrawalBalance,
              reservedDeposit,
              reservedWithdrawal,
              available: availableBalance,
              inFlight: inFlightBalance,
              total: totalBalance,
              currency: currency
            },
            successRate: Math.round(successRate),
            trend: config.trend
          });
        }
      });
    });

    return operators;
  };

  const transformDashboardData = (): OperatorData[] => {
    const operators: OperatorData[] = [];
    const requiredMethods = ['airtel', 'orange', 'mpesa', 'afrimoney'];
    
    const operatorMap = new Map();
    dashboardData!.by_operator.forEach(op => {
      const key = `${op.method.toLowerCase()}_${op.currency}`;
      operatorMap.set(key, op);
    });

    const balanceMap = new Map();
    balanceData!.balances.forEach(bal => {
      const key = `${bal.method.toLowerCase()}_${bal.currency}`;
      balanceMap.set(key, bal);
    });

    requiredMethods.forEach(method => {
      ['CDF', 'USD'].forEach(currency => {
        const key = `${method}_${currency}`;
        const operator = operatorMap.get(key);
        const operatorBalance = balanceMap.get(key);

        if (currency !== selectedCurrency) {
          return;
        }

        const depositBalance = operatorBalance?.realtime_deposit || 0;
        const withdrawalBalance = operatorBalance?.realtime_withdrawal || 0;
        const reservedDeposit = operatorBalance?.reserved_deposit || 0;
        const reservedWithdrawal = operatorBalance?.reserved_withdrawal || 0;
        const availableBalance = operatorBalance?.available !== undefined
          ? operatorBalance.available
          : (depositBalance - reservedDeposit) + (withdrawalBalance - reservedWithdrawal);
        const inFlightBalance = operatorBalance?.in_flight !== undefined
          ? operatorBalance.in_flight
          : reservedDeposit + reservedWithdrawal;
        const totalBalance = operatorBalance?.total !== undefined
          ? operatorBalance.total
          : depositBalance + withdrawalBalance;

        const config = operatorConfig[method] || getDefaultConfig();
        const successful = operator?.successful || 0;
        const failed = operator?.failed || 0;
        const pending = operator?.pending || 0;
        const submitted = operator?.submitted || 0;
        
        const totalTransactions = successful + failed;
        const successRate = totalTransactions > 0 ? (successful / totalTransactions) * 100 : 0;

        if (successful > 0 || failed > 0 || pending > 0 || submitted > 0 || totalBalance > 0 || selectedCurrency === currency) {
          operators.push({
            name: method.charAt(0).toUpperCase() + method.slice(1),
            gradient: config.gradient,
            stats: {
              successful,
              failed,
              pending,
              submitted
            },
            balance: {
              deposit: depositBalance,
              withdrawal: withdrawalBalance,
              reservedDeposit,
              reservedWithdrawal,
              available: availableBalance,
              inFlight: inFlightBalance,
              total: totalBalance,
              currency: currency
            },
            successRate: Math.round(successRate),
            trend: config.trend
          });
        }
      });
    });

    return operators;
  };

  const operators = getOperatorsFromBackendData();

  // Calculate chart data
  const getSuccessRateChartData = () => {
    return operators.map(op => ({
      operator: op.name,
      rate: op.successRate,
      color: operatorConfig[op.name.toLowerCase()]?.color || '#718096'
    }));
  };

  const getTransactionVolumeData = () => {
    return operators.map(op => ({
      operator: op.name,
      successful: op.stats.successful,
      failed: op.stats.failed,
      pending: op.stats.pending,
      color: operatorConfig[op.name.toLowerCase()]?.color || '#718096'
    }));
  };

  const getBalanceDistributionData = () => {
    return operators.map(op => ({
      operator: op.name,
      available: op.balance.available,
      inFlight: op.balance.inFlight,
      total: op.balance.total,
      color: operatorConfig[op.name.toLowerCase()]?.color || '#718096'
    }));
  };

  const getAvailableCurrencies = (): string[] => {
    const currencies = new Set<string>();
    
    if (operatorStatsData?.operator_stats) {
      operatorStatsData.operator_stats.forEach(op => {
        currencies.add(op.currency);
      });
    } 
    if (dashboardData?.by_operator) {
      dashboardData.by_operator.forEach(op => {
        currencies.add(op.currency);
      });
    }
      // 3. VÉRIFIE AUSSI BALANCE_DATA (IMPORTANT !)
  if (balanceData?.balances && balanceData.balances.length > 0) {
    balanceData.balances.forEach(bal => {
      if (bal.currency) currencies.add(bal.currency);
    });
  }
    
    return ['ALL', ...Array.from(currencies)];
  };

  const availableCurrencies = getAvailableCurrencies();

  // Simple chart components
  const BarChart = ({ data, title, height = 200 }: { data: any[], title: string, height?: number }) => {
    const maxValue = Math.max(...data.flatMap(d => [d.successful || 0, d.failed || 0, d.rate || 0]));
    
    return (
      <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
        <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <BarChart3 className="w-4 h-4 text-blue-600" />
          {title}
        </h3>
        <div className="space-y-3" style={{ height: `${height}px` }}>
          {data.map((item, index) => (
            <div key={index} className="flex items-center gap-3">
              <div className="w-20 text-xs text-gray-600 font-medium">{item.operator}</div>
              <div className="flex-1">
                <div 
                  className="h-5 rounded-full transition-all duration-300 hover:opacity-80"
                  style={{ 
                    width: `${((item.rate || item.successful) / maxValue) * 100}%`,
                    backgroundColor: item.color
                  }}
                ></div>
              </div>
              <div className="w-12 text-right text-xs font-semibold text-gray-700">
                {item.rate ? `${item.rate}%` : item.successful?.toLocaleString()}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const StackedBarChart = ({ data, title }: { data: any[], title: string }) => {
    const maxValue = Math.max(...data.map(d => d.successful + d.failed + d.pending));
    
    return (
      <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
        <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <TrendingUpIcon className="w-4 h-4 text-green-600" />
          {title}
        </h3>
        <div className="space-y-3">
          {data.map((item, index) => {
            const total = item.successful + item.failed + item.pending;
            return (
              <div key={index} className="flex items-center gap-3">
                <div className="w-20 text-xs text-gray-600 font-medium">{item.operator}</div>
                <div className="flex-1 h-6 bg-gray-100 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-green-500 float-left transition-all duration-300 hover:opacity-90"
                    style={{ width: `${(item.successful / total) * 100}%` }}
                    title={`Successful: ${item.successful}`}
                  ></div>
                  <div 
                    className="h-full bg-red-500 float-left transition-all duration-300 hover:opacity-90"
                    style={{ width: `${(item.failed / total) * 100}%` }}
                    title={`Failed: ${item.failed}`}
                  ></div>
                  <div 
                    className="h-full bg-yellow-500 float-left transition-all duration-300 hover:opacity-90"
                    style={{ width: `${(item.pending / total) * 100}%` }}
                    title={`Pending: ${item.pending}`}
                  ></div>
                </div>
                <div className="w-16 text-right text-xs font-semibold text-gray-700">
                  {total.toLocaleString()}
                </div>
              </div>
            );
          })}
          <div className="flex items-center justify-center gap-4 mt-2 text-xs text-gray-500">
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 bg-green-500 rounded"></div>
              <span>Successful</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 bg-red-500 rounded"></div>
              <span>Failed</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 bg-yellow-500 rounded"></div>
              <span>Pending</span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const BalanceChart = ({ data, title }: { data: any[], title: string }) => {
    return (
      <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
        <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <PieChart className="w-4 h-4 text-purple-600" />
          {title}
        </h3>
        <div className="space-y-4">
          {data.map((item, index) => (
            <div key={index} className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div 
                  className="w-3 h-3 rounded"
                  style={{ backgroundColor: item.color }}
                ></div>
                <span className="text-sm text-gray-700 font-medium">{item.operator}</span>
              </div>
              <div className="text-right">
                <div className="text-sm font-semibold text-gray-900">
                  {formatCurrency(item.total, selectedCurrency)}
                </div>
                <div className="text-xs text-gray-500">
                  {t('balances.available') || 'Disponible'}: {formatCurrency(item.available, selectedCurrency)}
                  {item.inFlight > 0 && (
                    <> • {t('balances.inFlight') || 'Réservé'}: {formatCurrency(item.inFlight, selectedCurrency)}</>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="p-4">
        <div className="animate-pulse">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between mb-6 gap-4">
            <div>
              <div className="h-6 bg-gray-200 rounded w-48 mb-2"></div>
              <div className="h-4 bg-gray-200 rounded w-36"></div>
            </div>
            <div className="h-10 bg-gray-200 rounded-lg w-32"></div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[...Array(4)].map((_, index) => (
              <div key={index} className="bg-gray-100 rounded-xl p-4 h-64"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4">
      {/* Enhanced Header with Tabs */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between mb-5 gap-4">
        <div>
          <h2 className="text-lg font-bold text-slate-900">Operators Performance</h2>
          <p className="text-slate-500 text-xs mt-0.5">
            Real-time analytics and insights
          </p>
        </div>

        <div className="flex flex-col sm:flex-row gap-2">
          {/* View toggle (Cards / Charts) */}
          <div className="inline-flex items-center bg-slate-100 ring-1 ring-slate-200 rounded-lg p-0.5">
            {(['cards', 'charts'] as const).map((tab) => {
              const active = activeTab === tab;
              return (
                <button
                  key={tab}
                  type="button"
                  onClick={() => setActiveTab(tab)}
                  className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-all ${
                    active
                      ? 'bg-white text-slate-900 shadow-sm ring-1 ring-slate-200'
                      : 'text-slate-500 hover:text-slate-900'
                  }`}
                >
                  {tab === 'cards' ? 'Cards' : 'Charts'}
                </button>
              );
            })}
          </div>

          {/* Currency switch (segmented control) */}
          <div className="inline-flex items-center bg-slate-100 ring-1 ring-slate-200 rounded-lg p-0.5">
            {['CDF', 'USD'].map((cur) => {
              const active = selectedCurrency === cur;
              return (
                <button
                  key={cur}
                  type="button"
                  onClick={() => setSelectedCurrency(cur)}
                  className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-all ${
                    active
                      ? 'bg-indigo-600 text-white shadow-sm shadow-indigo-500/30'
                      : 'text-slate-500 hover:text-slate-900'
                  }`}
                >
                  {cur}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {activeTab === 'cards' ? (
        /* Cards View */
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {operators.map((operator, index) => {
              const config = operatorConfig[operator.name.toLowerCase()] || getDefaultConfig();
              
              return (
                <button
                  type="button"
                  key={index}
                  onClick={() => setSelectedOperator(operator)}
                  className="group relative overflow-hidden rounded-xl transition-all duration-300 hover:scale-[1.02] hover:shadow-xl text-left w-full h-full"
                >
                  {/* Glow effect on hover */}
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

                  <div className="relative p-3 sm:p-3.5 text-white flex flex-col h-full bg-slate-900 ring-1 ring-white/[0.06] shadow-lg shadow-black/20">
                    {/* Operator color stripe (top) */}
                    <div
                      className="absolute top-0 left-0 right-0 h-[3px]"
                      style={{ background: config.gradient }}
                    />
                    {/* Click hint */}
                    <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                      <div className="flex items-center gap-1 text-[10px] text-white/80 bg-white/10 backdrop-blur-sm rounded-full px-2 py-0.5">
                        <Info className="w-3 h-3" />
                        <span>{t('balances.clickForDetails') || 'Détails'}</span>
                      </div>
                    </div>
                    {/* Compact Header */}
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-2.5">
                        <div className="w-9 h-9 bg-white/10 rounded-lg flex items-center justify-center backdrop-blur-sm border border-white/10">
                          <img
                            src={config.icon}
                            alt={operator.name}
                            className="w-5 h-5 object-contain"
                            onError={(e) => {
                              e.currentTarget.style.display = 'none';
                            }}
                          />
                        </div>
                        <div>
                          <h3 className="text-sm font-bold text-white">{operator.name}</h3>
                          <div className="flex items-center gap-1 mt-0.5">
                            <span className="text-slate-400 text-[11px]">{operator.balance.currency}</span>
                            <div className="w-1 h-1 bg-slate-500 rounded-full"></div>
                            <span className="text-slate-400 text-[11px]">{operator.trend}% ↗</span>
                          </div>
                        </div>
                      </div>

                      <div className="text-right">
                        <div className="text-base font-black text-white">{operator.successRate}%</div>
                        <div className="text-slate-400 text-[10px] uppercase tracking-wide">Success Rate</div>
                      </div>
                    </div>

                    {/* Balance Section */}
                    <div className="mb-3 p-2.5 bg-white/[0.04] rounded-lg backdrop-blur-sm border border-white/[0.06]">
                      <div className="flex items-center justify-between mb-2.5">
                        <div className="flex items-center gap-1.5">
                          <Wallet className="w-3 h-3 text-slate-400" />
                          <span className="text-slate-300 font-semibold text-[11px]">{t('balances.available') || 'Disponible'}</span>
                        </div>
                        <span className="text-sm font-bold text-white tabular-nums">
                          {formatCurrency(operator.balance.available, operator.balance.currency)}
                        </span>
                      </div>

                      <div className="space-y-1.5">
                        {operator.balance.inFlight > 0 && (
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-1.5">
                              <Clock className="w-3 h-3 text-amber-300" />
                              <span className="text-slate-400 text-[11px]">{t('balances.inFlight') || 'Réservé'}</span>
                            </div>
                            <span className="text-right font-semibold text-amber-200 text-[11px] tabular-nums">
                              {formatCurrency(operator.balance.inFlight, operator.balance.currency)}
                            </span>
                          </div>
                        )}

                        <div className="flex items-center justify-between pt-1 border-t border-white/[0.06]">
                          <span className="text-slate-500 text-[10px] uppercase tracking-wide">{t('balances.total') || 'Total'}</span>
                          <span className="text-right font-semibold text-slate-200 text-[11px] tabular-nums">
                            {formatCurrency(operator.balance.total, operator.balance.currency)}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Statistics */}
                    <div className="space-y-1.5">
                      <h4 className="text-slate-400 font-semibold text-[10px] uppercase tracking-wider">Transactions</h4>
                      <div className="grid grid-cols-2 gap-1.5">
                        <div className="text-center px-2 py-1.5 bg-white/[0.04] rounded border border-white/[0.06]">
                          <div className="text-sm font-bold text-emerald-300 tabular-nums">{operator.stats.successful.toLocaleString()}</div>
                          <div className="text-slate-500 text-[10px]">Successful</div>
                        </div>
                        <div className="text-center px-2 py-1.5 bg-white/[0.04] rounded border border-white/[0.06]">
                          <div className="text-sm font-bold text-rose-300 tabular-nums">{operator.stats.failed.toLocaleString()}</div>
                          <div className="text-slate-500 text-[10px]">Failed</div>
                        </div>
                        <div className="text-center px-2 py-1.5 bg-white/[0.04] rounded border border-white/[0.06]">
                          <div className="text-sm font-bold text-amber-300 tabular-nums">{operator.stats.pending.toLocaleString()}</div>
                          <div className="text-slate-500 text-[10px]">Pending</div>
                        </div>
                        <div className="text-center px-2 py-1.5 bg-white/[0.04] rounded border border-white/[0.06]">
                          <div className="text-sm font-bold text-sky-300 tabular-nums">{operator.stats.submitted.toLocaleString()}</div>
                          <div className="text-slate-500 text-[10px]">Submitted</div>
                        </div>
                      </div>
                    </div>

                    {/* Detailed by Action */}
                    {operator.stats.deposit && operator.stats.withdrawal && (
                      <div className="mt-2.5 pt-2.5 border-t border-white/[0.06]">
                        <h4 className="text-slate-400 font-semibold text-[10px] uppercase tracking-wider mb-1.5">Detailed by Action</h4>
                        <div className="grid grid-cols-2 gap-1.5 text-[11px]">
                          <div className="bg-white/[0.03] rounded p-1.5 border border-white/[0.04]">
                            <div className="text-emerald-300 font-semibold mb-0.5 text-[10px]">Deposits</div>
                            <div className="flex justify-between">
                              <span className="text-slate-500">Success:</span>
                              <span className="text-emerald-300 font-semibold tabular-nums">{operator.stats.deposit.successful}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-slate-500">Failed:</span>
                              <span className="text-rose-300 font-semibold tabular-nums">{operator.stats.deposit.failed}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-slate-500">Pending:</span>
                              <span className="text-amber-300 font-semibold tabular-nums">{operator.stats.deposit.pending}</span>
                            </div>
                          </div>
                          <div className="bg-white/[0.03] rounded p-1.5 border border-white/[0.04]">
                            <div className="text-sky-300 font-semibold mb-0.5 text-[10px]">Withdrawals</div>
                            <div className="flex justify-between">
                              <span className="text-slate-500">Success:</span>
                              <span className="text-emerald-300 font-semibold tabular-nums">{operator.stats.withdrawal.successful}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-slate-500">Failed:</span>
                              <span className="text-rose-300 font-semibold tabular-nums">{operator.stats.withdrawal.failed}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-slate-500">Pending:</span>
                              <span className="text-amber-300 font-semibold tabular-nums">{operator.stats.withdrawal.pending}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Enhanced shadow effect */}
                  <div className="absolute inset-0 rounded-xl shadow-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 -z-10"></div>
                </button>
              );
            })}
          </div>

        </>
      ) : (
        /* Charts View */
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Success Rate Comparison */}
          <BarChart 
            data={getSuccessRateChartData()} 
            title="Success Rate by Operator"
            height={160}
          />

          {/* Transaction Volume */}
          <StackedBarChart 
            data={getTransactionVolumeData()} 
            title="Transaction Volume Distribution"
          />

          {/* Balance Overview */}
          <BalanceChart 
            data={getBalanceDistributionData()} 
            title="Balance Distribution by Operator"
          />

          {/* Performance Summary */}
          <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200 lg:col-span-2">
            <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <TrendingUpIcon className="w-4 h-4 text-blue-600" />
              Performance Insights
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-3">
                <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Top Performers</h4>
                {operators
                  .sort((a, b) => b.successRate - a.successRate)
                  .slice(0, 2)
                  .map((op, index) => (
                    <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                      <span className="text-sm font-medium text-gray-900">{op.name}</span>
                      <span className="text-sm font-semibold text-green-600">{op.successRate}%</span>
                    </div>
                  ))
                }
              </div>
              <div className="space-y-3">
                <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Highest Volume</h4>
                {operators
                  .sort((a, b) => (b.stats.successful + b.stats.failed) - (a.stats.successful + a.stats.failed))
                  .slice(0, 2)
                  .map((op, index) => (
                    <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                      <span className="text-sm font-medium text-gray-900">{op.name}</span>
                      <span className="text-sm font-semibold text-blue-600">
                        {(op.stats.successful + op.stats.failed).toLocaleString()}
                      </span>
                    </div>
                  ))
                }
              </div>
            </div>
          </div>
        </div>
      )}

      {/* No operators message */}
      {operators.length === 0 && !loading && (
        <div className="text-center py-8">
          <div className="text-gray-500 text-sm">
            {selectedCurrency === 'CDF'
              ? "No operators found for CDF"
              : `No operators found for ${selectedCurrency}`
            }
          </div>
        </div>
      )}

      <WalletDetailsModal
        data={
          selectedOperator
            ? (() => {
                const cfg =
                  operatorConfig[selectedOperator.name.toLowerCase()] || getDefaultConfig();
                const b = selectedOperator.balance;
                return {
                  name: selectedOperator.name,
                  currency: b.currency,
                  deposit: b.deposit,
                  withdrawal: b.withdrawal,
                  reservedDeposit: b.reservedDeposit,
                  reservedWithdrawal: b.reservedWithdrawal,
                  available: b.available,
                  inFlight: b.inFlight,
                  total: b.total,
                  gradient: cfg.gradient,
                  iconUrl: cfg.icon,
                };
              })()
            : null
        }
        onClose={() => setSelectedOperator(null)}
      />
    </div>
  );
};

export default OperatorStats;